<template>
  <div
    ref="gutter"
    class="p-splitter-gutter"
    role="separator"
    tabindex="-1"
    :style="_this.gutterStyle"
    @mousedown="onGutterMouseDown($event, index)"
    @touchstart="onGutterTouchStart($event, index)"
    @touchmove="onGutterTouchMove($event, index)"
    @touchend="onGutterTouchEnd($event, index)"
    :data-p-gutter-resizing="false"
  >
    <div
      class="p-splitter-gutter-handle"
      tabindex="0"
      :aria-orientation="layout"
      :aria-valuenow="prevSize"
      @keyup="onGutterKeyUp"
      @keydown="onGutterKeyDown($event, index)"
    ></div>
  </div>
</template>

<script>
import { DomHandler, ObjectUtils } from "primevue/utils";
import SplitterVue from "./Splitter.vue";

export default {
  name: "SplitterGutter",
  mixins: [SplitterVue],
  props: {
    index: { default: 0 },
    _this: { default: {} },
    eventElement: { default: () => PointerEvent },
    eventParentElement: {}
  },
  data() {
    return {
      prevSize: null,
      dragging: false,
      mouseMoveListener: null,
      mouseUpListener: null,
      touchMoveListener: null,
      touchEndListener: null,
      gutterElement: null,
      startPos: null,
      prevPanelElement: null,
      nextPanelElement: null,
      nextPanelSize: null,
      prevPanelSize: null,
      prevPanelIndex: null,
      timer: null,
      sizeGutter: null,
      newEventElement: null,
      newEvent: null
    };
  },
  beforeDestroy() {
    this.clear();
    this.unbindMouseListeners();
  },
  watch: {
    eventElement(event) {
      this.newEvent = event;
      this.setupPanelLayout();
      this.onResizeStart(event, this.index);
      this.onResize(event, null, true);
    }
  },
  methods: {
    onResizeStart(event, index, isKeyDown) {
      this.gutterElement = this.eventParentElement
        ? this.eventParentElement
        : event.currentTarget || event.target.parentElement;

      this.sizeGutter = this._this.horizontal
        ? DomHandler.getWidth(this._this.$el)
        : DomHandler.getHeight(this._this.$el);

      if (!isKeyDown) {
        this.dragging = true;
        this.startPos =
          this._this.layout === "horizontal"
            ? event.pageX || event.changedTouches[0].pageX
            : event.pageY || event.changedTouches[0].pageY;
      }

      this.prevPanelElement = this.gutterElement.previousElementSibling;
      this.nextPanelElement = this.gutterElement.nextElementSibling;

      if (isKeyDown) {
        this.prevPanelSize = this._this.horizontal
          ? DomHandler.getOuterWidth(this.prevPanelElement, true)
          : DomHandler.getOuterHeight(this.prevPanelElement, true);
        this.nextPanelSize = this._this.horizontal
          ? DomHandler.getOuterWidth(this.nextPanelElement, true)
          : DomHandler.getOuterHeight(this.nextPanelElement, true);
      } else {
        this.prevPanelSize =
          (100 *
            (this._this.horizontal
              ? DomHandler.getOuterWidth(this.prevPanelElement, true)
              : DomHandler.getOuterHeight(this.prevPanelElement, true))) /
          this.sizeGutter;
        this.nextPanelSize =
          (100 *
            (this._this.horizontal
              ? DomHandler.getOuterWidth(this.nextPanelElement, true)
              : DomHandler.getOuterHeight(this.nextPanelElement, true))) /
          this.sizeGutter;
      }
      this.prevPanelIndex = index;
      this.$emit("resizestart", {
        originalEvent: event,
        sizes: this._this.panelSizes,
        panels: this._this.panels
      });
      this.$refs.gutter.setAttribute("data-p-gutter-resizing", true);
      this._this.$el.setAttribute("data-p-resizing", true);
    },
    onResize(event, step, isRestore, isKeyDown) {
      let newPos, newPrevPanelSize, newNextPanelSize;

      if (isKeyDown) {
        if (this._this.horizontal) {
          newPrevPanelSize =
            (100 * (this.prevPanelSize + step)) / this.sizeGutter;
          newNextPanelSize =
            (100 * (this.nextPanelSize - step)) / this.sizeGutter;
        } else {
          newPrevPanelSize =
            (100 * (this.prevPanelSize - step)) / this.sizeGutter;
          newNextPanelSize =
            (100 * (this.nextPanelSize + step)) / this.sizeGutter;
        }
      } else {
        if (this._this.horizontal) {
          newPos =
            (event.pageX * 100) / this.sizeGutter -
            (this.startPos * 100) / this.sizeGutter;
        } else {
          newPos =
            (event.pageY * 100) / this.sizeGutter -
            (this.startPos * 100) / this.sizeGutter;
        }
        newPrevPanelSize = this.prevPanelSize + newPos;
        newNextPanelSize = this.nextPanelSize - newPos;
      }

      this.prevSize = parseFloat(newPrevPanelSize).toFixed(4);
      if (this.validateResize(newPrevPanelSize, newNextPanelSize)) {
        const calcPrevPanelElement =
          "calc(" +
          newPrevPanelSize +
          "% - " +
          (this._this.panels.length - 1) * this.gutterSize +
          "px)";
        const calcNextPanelElement =
          "calc(" +
          newNextPanelSize +
          "% - " +
          (this._this.panels.length - 1) * this.gutterSize +
          "px)";
        if (!isRestore) {
          this.prevPanelElement.style.flexBasis = calcPrevPanelElement;
          this.nextPanelElement.style.flexBasis = calcNextPanelElement;
          this._this.panelSizes[this.prevPanelIndex] = newPrevPanelSize;
          this._this.panelSizes[this.prevPanelIndex + 1] = newNextPanelSize;

          if (this._this.layout !== "horizontal") {
            this.prevPanelElement.style.height = calcPrevPanelElement;
            this.nextPanelElement.style.height = calcNextPanelElement;
          } else {
            this.prevPanelElement.style.width = calcPrevPanelElement;
            this.nextPanelElement.style.width = calcNextPanelElement;
          }
        }
      }
      this.$emit("onResize", {
        originalEvent: event,
        sizes: this._this.panelSizes,
        panels: this._this.panels
      });
    },
    onResizeEnd(event) {
      if (this._this.isStateful()) {
        this.saveState();
      }
      this.$emit("resizeend", {
        originalEvent: event,
        sizes: this._this.panelSizes,
        panels: this._this.panels
      });
      this.$refs.gutter.setAttribute("data-p-gutter-resizing", false);
      this._this.$el.setAttribute("data-p-resizing", false);
      this.clear();
    },
    repeat(event, index, step) {
      this.onResizeStart(event, index, true);
      this.onResize(event, step, true);
    },
    setTimer(event, index, step) {
      this.clearTimer();
      this.timer = setTimeout(() => {
        this.repeat(event, index, step);
      }, 40);
    },
    clearTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
      }
    },
    unbindMouseListeners() {
      if (this.mouseMoveListener) {
        document.removeEventListener("mousemove", this.mouseMoveListener);
        this.mouseMoveListener = null;
      }

      if (this.mouseUpListener) {
        document.removeEventListener("mouseup", this.mouseUpListener);
        this.mouseUpListener = null;
      }
    },
    clear() {
      this.dragging = false;
      this.sizeGutter = null;
      this.startPos = null;
      this.prevPanelElement = null;
      this.nextPanelElement = null;
      this.prevPanelSize = null;
      this.nextPanelSize = null;
      this.gutterElement = null;
      this.prevPanelIndex = null;
    },
    onGutterKeyUp() {
      this.clearTimer();
      this.onResizeEnd();
    },
    onGutterKeyDown(event, index) {
      switch (event.code) {
        case "ArrowLeft": {
          if (this._this.layout === "horizontal") {
            this.setTimer(event, index, this.step * -1);
          }

          event.preventDefault();
          break;
        }

        case "ArrowRight": {
          if (this._this.layout === "horizontal") {
            this.setTimer(event, index, this.step);
          }

          event.preventDefault();
          break;
        }

        case "ArrowDown": {
          if (this._this.layout === "vertical") {
            this.setTimer(event, index, this.step * -1);
          }

          event.preventDefault();
          break;
        }

        case "ArrowUp": {
          if (this._this.layout === "vertical") {
            this.setTimer(event, index, this.step);
          }

          event.preventDefault();
          break;
        }

        default:
          //no op
          break;
      }
    },
    onGutterMouseDown(event, index) {
      this.onResizeStart(event, index);
      this.bindMouseListeners();
    },
    onGutterTouchStart(event, index) {
      this.onResizeStart(event, index);
      this.bindTouchListeners();
      event.preventDefault();
    },
    onGutterTouchMove(event) {
      this.onResize(event);
      event.preventDefault();
    },
    onGutterTouchEnd(event) {
      this.onResizeEnd(event);
      this.unbindTouchListeners();
      event.preventDefault();
    },
    bindMouseListeners() {
      if (!this.mouseMoveListener) {
        this.mouseMoveListener = event => this.onResize(event);
        document.addEventListener("mousemove", this.mouseMoveListener);
      }

      if (!this.mouseUpListener) {
        this.mouseUpListener = event => {
          this.onResizeEnd(event);
          this.unbindMouseListeners();
        };

        document.addEventListener("mouseup", this.mouseUpListener);
      }
    },
    bindTouchListeners() {
      if (!this.touchMoveListener) {
        this.touchMoveListener = event =>
          this.onResize(event.changedTouches[0]);
        document.addEventListener("touchmove", this.touchMoveListener);
      }

      if (!this.touchEndListener) {
        this.touchEndListener = event => {
          this.resizeEnd(event);
          this.unbindTouchListeners();
        };

        document.addEventListener("touchend", this.touchEndListener);
      }
    },
    validateResize(newPrevPanelSize, newNextPanelSize) {
      let prevPanelMinSize = ObjectUtils.getVNodeProp(
        this._this.panels[0],
        "minSize"
      );
      if (
        this._this.panels[0] &&
        prevPanelMinSize &&
        prevPanelMinSize > newPrevPanelSize
      ) {
        return false;
      }

      let newPanelMinSize = ObjectUtils.getVNodeProp(
        this._this.panels[1],
        "minSize"
      );
      if (
        this._this.panels[1] &&
        newPanelMinSize &&
        newPanelMinSize > newNextPanelSize
      ) {
        return false;
      }

      return true;
    },
    unbindTouchListeners() {
      if (this.touchMoveListener) {
        document.removeEventListener("touchmove", this.touchMoveListener);
        this.touchMoveListener = null;
      }

      if (this.touchEndListener) {
        document.removeEventListener("touchend", this.touchEndListener);
        this.touchEndListener = null;
      }
    },
    getStorage() {
      switch (this.stateStorage) {
        case "local":
          return window.localStorage;

        case "session":
          return window.sessionStorage;

        default:
          throw new Error(
            this.stateStorage +
              ' is not a valid value for the state storage, supported values are "local" and "session".'
          );
      }
    },
    saveState() {
      this.getStorage().setItem(
        this.stateKey,
        JSON.stringify(this._this.panelSizes)
      );
    }
  }
};
</script>

<style></style>
